import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from 'mdi-react/CloseIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import TagOutlineIcon from 'mdi-react/TagOutlineIcon';
import { arrayOf, bool, func, object } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  paper: { borderRadius: '16px', maxWidth: 640, width: '100%' },
  title: {
    padding: theme.spacing(2, 3, 1.5, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleText: { fontSize: theme.typography.pxToRem(18), fontWeight: 700 },
  content: {
    padding: theme.spacing(0, 3, 3, 3),
    '&.MuiDialogContent-root': { paddingTop: theme.spacing(1) },
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  search: { flex: 1 },
  createBtn: { textTransform: 'none', whiteSpace: 'nowrap' },
  groupHeader: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 0, 0.5, 0.5),
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxHeight: 480,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: '8px',
    backgroundColor: theme.palette.background.default,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  name: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(13),
  },
});

const ManageTagsDialog = ({
  open,
  tags = [],
  attachedIds = [],
  onClose,
  onSaveAttachments,
  onCreateTag,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [search, setSearch] = useState('');
  const [localIds, setLocalIds] = useState([]);

  useEffect(() => {
    if (open) {
      setLocalIds((attachedIds || []).map(String));
      setSearch('');
    }
  }, [open, attachedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter(
      t =>
        (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q),
    );
  }, [tags, search]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    filtered.forEach(t => {
      const cat = t.category || 'General';
      if (!buckets.has(cat)) buckets.set(cat, []);
      buckets.get(cat).push(t);
    });
    return Array.from(buckets.entries()).map(([category, list]) => ({
      category,
      list: list.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    }));
  }, [filtered]);

  const toggle = id => {
    const sid = String(id);
    setLocalIds(prev => (prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]));
  };

  const dirty = useMemo(() => {
    const base = (attachedIds || []).map(String).sort().join('|');
    const cur = [...localIds].sort().join('|');
    return base !== cur;
  }, [attachedIds, localIds]);

  const handleClose = () => {
    if (dirty) onSaveAttachments(localIds);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: styles.paper } }}
    >
      <DialogTitle sx={styles.title} disableTypography>
        <Typography component="span" sx={styles.titleText}>
          {I18n.t('AgenticChatFlow.TagsManageTitle')}
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label={I18n.t('Common.close')}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Box sx={styles.toolbar}>
          <TextField
            sx={styles.search}
            size="small"
            placeholder={I18n.t('AgenticChatFlow.TagsSearchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyIcon size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            sx={styles.createBtn}
            startIcon={<PlusIcon size={16} />}
            onClick={() => {
              // Persist current local state first so the new tag isn't lost when
              // the parent reopens this dialog after EditTagDialog closes.
              if (dirty) onSaveAttachments(localIds);
              onCreateTag();
            }}
          >
            {I18n.t('AgenticChatFlow.TagsCreate')}
          </Button>
        </Box>
        {grouped.length === 0 && (
          <Box sx={styles.emptyState}>{I18n.t('AgenticChatFlow.TagsManageEmpty')}</Box>
        )}
        {grouped.map(({ category, list }) => (
          <Box key={category}>
            <Typography sx={styles.groupHeader}>{category}</Typography>
            <Box sx={styles.list}>
              {list.map(t => {
                const attached = localIds.includes(String(t._id));
                return (
                  <Box key={t._id} sx={styles.row}>
                    <Box component="span" sx={styles.iconWrap}>
                      <TagOutlineIcon size={16} />
                    </Box>
                    <Typography sx={[styles.name, { flex: 1, minWidth: 0 }]} noWrap>
                      {t.name}
                    </Typography>
                    {attached ? (
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => toggle(t._id)}
                      >
                        {I18n.t('AgenticChatFlow.Detach')}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        disableElevation
                        onClick={() => toggle(t._id)}
                      >
                        {I18n.t('AgenticChatFlow.Attach')}
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

ManageTagsDialog.propTypes = {
  open: bool.isRequired,
  tags: arrayOf(object),
  attachedIds: arrayOf(object),
  onClose: func.isRequired,
  onSaveAttachments: func.isRequired,
  onCreateTag: func.isRequired,
};

export default ManageTagsDialog;
